import { createSlice } from '@reduxjs/toolkit';
import { RequestState } from '../../shared/types/request-state';
import { RequestStatus } from '../../shared/enums/request-status';
import {
  addRemoveLinkedEmailAccountsInInboxAnalyzerRequest,
  draftSeedTestRequest,
  getAllNotLinkedEmailAccountsInInboxRadarRequest,
  getLinkedEmailAccountsInInboxAnalyzerRequest,
  getNotLinkedEmailAccountsCountInInboxRadarRequest,
  getSeedTestRequest,
  seedTestRequest,
  updateSeedTestRequest,
  getTestGraphDataRequest,
  getTestSummaryDataRequest,
  getTestListRequest,
  getTestReportGraphDataRequest,
  getTestReportSummaryDataRequest,
  getTestReportListRequest,
  getTestReportEmailsRequest,
  getTestConfigRequest,
  updateTestStatusRequest,
  reRunTestRequest,
  getTestPreviewEmailRequest,
  getTestReportExportRequest,
  getSequenceSubsequenceListRequest,
  getShareableReportDataRequest,
  getShareableReportSenderEmailAccountsRequest,
  getSequenceStepListRequest,
  deleteTestReportRequest,
  getSeedTestTemplateReviewSendingLimitRequest,
  getSeedTestTemplateWarningBannerConfigRequest,
  getEmailReputationRequest,
  getSpammyEmailAccountsRequest,
  getSpamAssassinReportRequest,
  getTestReportEspsRequest,
} from './extra-actions';
import { transformGraphDataResponse } from './components/ir-graph/utils/helper';
import {
  CreateTestResponse,
  DashboardTestListResponse,
  CreateUpdateTest,
  DraftSeedTestPayload,
  GetAllNotLinkedEmailAccountsInInboxAnalyzerResponse,
  GetEmailAuthenticationResponse,
  GetLinkedEmailAccountsInInboxAnalyzerResponse,
  GetSeedTestReviewSendingLimitResponse,
  GetSeedTestWarningBannerConfigResponse,
  TestConfigResponse,
  TestGraphParseData,
  TestGraphResponse,
  TestPreviewEmailResponse,
  TestReportEmailsResponse,
  TestReportEspsResponse,
  TestReportGraphResponse,
  TestReportLastRunList,
  TestReportSummaryResponse,
  TestSummaryResponse,
} from './types';

type State = {
  getTestGraphDataRequest: RequestState;
  seedTestRequest: RequestState;
  getSeedTestRequest: RequestState;
  updateSeedTestRequest: RequestState;
  getSeedTestTemplateReviewSendingLimitRequest: RequestState;
  getSeedTestTemplateWarningBannerConfigRequest: RequestState;
  getAllNotLinkedEmailAccountsInInboxRadarRequest: RequestState;
  getNotLinkedEmailAccountsCountInInboxRadarRequest: RequestState;
  getLinkedEmailAccountsInInboxAnalyzerRequest: RequestState;
  addRemoveLinkedEmailAccountRequest: RequestState;
  draftSeedTestRequest: RequestState;
  getTestSummaryDataRequest: RequestState;
  getTestListRequest: RequestState;
  updateTestStatusRequest: RequestState;
  reRunTestRequest: RequestState;
  getTestConfigRequest: RequestState;
  getTestReportGraphDataRequest: RequestState;
  getTestReportSummaryDataRequest: RequestState;
  getTestReportListRequest: RequestState;
  getTestReportEmailsRequest: RequestState;
  getTestPreviewEmailRequest: RequestState;
  getTestReportExportRequest: RequestState;
  getSequenceSubsequenceListRequest: RequestState;
  getSequenceStepListRequest: RequestState;
  getShareableReportDataRequest: RequestState;
  getShareableReportSenderEmailAccountsRequest: RequestState;
  deleteTestReportRequest: RequestState;
  getEmailReputationRequest: RequestState;
  getSpammyEmailAccountsRequest: RequestState;
  getSpamAssassinReportRequest: RequestState;
  getTestReportEspsRequest: RequestState;

  createUpdateTestResponse: CreateTestResponse;
  getCreateUpdateTestResponse: CreateUpdateTest;
  getSeedTestTemplateReviewSendingLimitResponse: GetSeedTestReviewSendingLimitResponse;
  getSeedTestTemplateWarningBannerConfigResponse: GetSeedTestWarningBannerConfigResponse;
  getAllNotLinkedEmailAccountsInInboxRadarResponse: GetAllNotLinkedEmailAccountsInInboxAnalyzerResponse;
  getLinkedEmailAccountsInboxAnalyzerResponse: GetLinkedEmailAccountsInInboxAnalyzerResponse;
  getNotLinkedEmailAccountsCountInInboxRadarResponse: any;
  draftSeedTestResponse: DraftSeedTestPayload;

  testGraphDataResponse: TestGraphResponse;
  testGraphData: TestGraphParseData;
  testSummaryData: TestSummaryResponse;
  testListData: DashboardTestListResponse;
  updateTestStatusResponse: any;
  reRunTestResponse: CreateUpdateTest;

  testConfigResponse: TestConfigResponse;
  testReportGraphDataResponse: TestReportGraphResponse;
  testReportGraphData: TestGraphParseData;
  testReportLastRunList: TestReportLastRunList;
  testReportSummaryDataResponse: TestReportSummaryResponse;
  testReportListData: any;
  testReportEmailsResponse: TestReportEmailsResponse;
  testPreviewEmailResponse: TestPreviewEmailResponse;
  testReportExportResponse: any;
  sequenceSubsequences: any[];
  sequenceSteps: any[];
  spammyEmailAccountsResponse: any;
  testReportEspsResponse: TestReportEspsResponse;

  getShareableReportDataResponse: any;
  getShareableReportSenderEmailAccountsResponse: any;
  emailReputationResponse: GetEmailAuthenticationResponse;
  hasMoreEmailReputation: any;
  spamAssassinReportResponse: any;

  // Firebase
  isFireBaseConnected: boolean;
};

const initialState: State = {
  getTestGraphDataRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTestSummaryDataRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  seedTestRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSeedTestRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateSeedTestRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSeedTestTemplateReviewSendingLimitRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSeedTestTemplateWarningBannerConfigRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  reRunTestRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getAllNotLinkedEmailAccountsInInboxRadarRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getNotLinkedEmailAccountsCountInInboxRadarRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getLinkedEmailAccountsInInboxAnalyzerRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  addRemoveLinkedEmailAccountRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  draftSeedTestRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTestListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateTestStatusRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTestConfigRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTestReportGraphDataRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTestReportSummaryDataRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTestReportListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTestReportEmailsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTestPreviewEmailRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTestReportExportRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSequenceSubsequenceListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSequenceStepListRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getShareableReportDataRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getShareableReportSenderEmailAccountsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  deleteTestReportRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getEmailReputationRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSpammyEmailAccountsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSpamAssassinReportRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getTestReportEspsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },

  testGraphDataResponse: null,
  testGraphData: null,
  testSummaryData: null,
  testListData: null,
  updateTestStatusResponse: null,
  reRunTestResponse: null,
  createUpdateTestResponse: null,
  getCreateUpdateTestResponse: null,
  getSeedTestTemplateReviewSendingLimitResponse: null,
  getSeedTestTemplateWarningBannerConfigResponse: null,
  getAllNotLinkedEmailAccountsInInboxRadarResponse: null,
  getNotLinkedEmailAccountsCountInInboxRadarResponse: null,
  getLinkedEmailAccountsInboxAnalyzerResponse: null,
  draftSeedTestResponse: null,
  testConfigResponse: null,
  testReportGraphDataResponse: null,
  testReportGraphData: null,
  testReportLastRunList: null,
  testReportSummaryDataResponse: null,
  testReportListData: null,
  testReportEmailsResponse: null,
  testPreviewEmailResponse: null,
  testReportExportResponse: null,
  sequenceSubsequences: [],
  sequenceSteps: [],
  spammyEmailAccountsResponse: null,
  testReportEspsResponse: null,

  getShareableReportDataResponse: null,
  getShareableReportSenderEmailAccountsResponse: null,
  emailReputationResponse: null,
  hasMoreEmailReputation: null,
  spamAssassinReportResponse: null,

  isFireBaseConnected: false,
};

const inboxRadarSlice = createSlice({
  name: 'inboxRadar',
  initialState,
  reducers: {
    resetTestGraphData: (state) => {
      state.getTestGraphDataRequest = initialState.getTestGraphDataRequest;
      state.testGraphDataResponse = initialState.testGraphDataResponse;
      state.testGraphData = initialState.testGraphData;
    },
    resetTestSummaryData: (state) => {
      state.getTestSummaryDataRequest = initialState.getTestSummaryDataRequest;
      state.testSummaryData = initialState.testSummaryData;
    },
    resetTestListRequest: (state) => {
      state.getTestListRequest = initialState.getTestListRequest;
    },
    resetCreateUpdateTestResponse: (state) => {
      state.seedTestRequest = initialState.seedTestRequest;
      state.createUpdateTestResponse = initialState.createUpdateTestResponse;
    },
    resetGetCreateUpdateTestResponse: (state) => {
      state.getSeedTestRequest = initialState.getSeedTestRequest;
      state.getCreateUpdateTestResponse =
        initialState.getCreateUpdateTestResponse;
    },
    resetLinkInboxAnalyzerEmailAccountsState: (state) => {
      state.addRemoveLinkedEmailAccountRequest =
        initialState.addRemoveLinkedEmailAccountRequest;
    },
    resetDraftSeedTestState: (state) => {
      state.draftSeedTestRequest = initialState.draftSeedTestRequest;
    },
    resetUpdateSeedTestState: (state) => {
      state.updateSeedTestRequest = initialState.updateSeedTestRequest;
    },
    resetGetSeedTestTemplateReviewSendingLimitRequest: (state) => {
      state.getSeedTestTemplateReviewSendingLimitRequest =
        initialState.getSeedTestTemplateReviewSendingLimitRequest;
      state.getSeedTestTemplateReviewSendingLimitResponse =
        initialState.getSeedTestTemplateReviewSendingLimitResponse;
    },
    resetGetSeedTestTemplateWarningBannerConfigRequest: (state) => {
      state.getSeedTestTemplateWarningBannerConfigRequest =
        initialState.getSeedTestTemplateWarningBannerConfigRequest;
      state.getSeedTestTemplateWarningBannerConfigResponse =
        initialState.getSeedTestTemplateWarningBannerConfigResponse;
    },
    resetUpdateTestStatusRequest: (state) => {
      state.updateTestStatusRequest = initialState.updateTestStatusRequest;
      state.updateTestStatusResponse = initialState.updateTestStatusResponse;
    },
    resetReRunTestRequest: (state) => {
      state.reRunTestRequest = initialState.reRunTestRequest;
      state.reRunTestResponse = initialState.reRunTestResponse;
    },
    resetTestConfigRequest: (state) => {
      state.getTestConfigRequest = initialState.getTestConfigRequest;
      state.testConfigResponse = initialState.testConfigResponse;
      state.testReportLastRunList = initialState.testReportLastRunList;
    },
    resetTestReportGraphRequest: (state) => {
      state.getTestReportGraphDataRequest =
        initialState.getTestReportGraphDataRequest;
      state.testReportGraphDataResponse =
        initialState.testReportGraphDataResponse;
      state.testReportGraphData = initialState.testReportGraphData;
    },
    resetTestReportSummaryRequest: (state) => {
      state.getTestReportSummaryDataRequest =
        initialState.getTestReportSummaryDataRequest;
      state.testReportSummaryDataResponse =
        initialState.testReportSummaryDataResponse;
    },
    resetTestReportListRequest: (state) => {
      state.getTestReportListRequest = initialState.getTestReportListRequest;
      state.testReportListData = initialState.testReportListData;
    },
    resetTestReportEmailsRequest: (state) => {
      state.getTestReportEmailsRequest =
        initialState.getTestReportEmailsRequest;
      state.testReportEmailsResponse = initialState.testReportEmailsResponse;
    },
    resetTestPreviewEmailRequest: (state) => {
      state.getTestPreviewEmailRequest =
        initialState.getTestPreviewEmailRequest;
      state.testPreviewEmailResponse = initialState.testPreviewEmailResponse;
    },
    resetTestReportExportRequest: (state) => {
      state.getTestReportExportRequest =
        initialState.getTestReportExportRequest;
      state.testReportExportResponse = initialState.testReportExportResponse;
    },
    resetGetShareableReportDataRequest: (state) => {
      state.getShareableReportDataRequest =
        initialState.getShareableReportDataRequest;
      state.getShareableReportDataResponse =
        initialState.getShareableReportDataResponse;
    },
    resetGetShareableReportSenderEmailAccountsRequest: (state) => {
      state.getShareableReportSenderEmailAccountsRequest =
        initialState.getShareableReportSenderEmailAccountsRequest;
      state.getShareableReportSenderEmailAccountsResponse =
        initialState.getShareableReportSenderEmailAccountsResponse;
    },
    resetDeleteTestReportRequest: (state) => {
      state.deleteTestReportRequest = initialState.deleteTestReportRequest;
    },
    resetEmailReputationRequest: (state) => {
      state.getEmailReputationRequest = initialState.getEmailReputationRequest;
      state.emailReputationResponse = initialState.emailReputationResponse;
      state.hasMoreEmailReputation = initialState.hasMoreEmailReputation;
    },
    resetSequenceStepList: (state) => {
      state.getSequenceStepListRequest =
        initialState.getSequenceStepListRequest;
      state.sequenceSteps = initialState.sequenceSteps;
    },
    resetSequenceSubsequenceList: (state) => {
      state.getSequenceSubsequenceListRequest =
        initialState.getSequenceSubsequenceListRequest;
      state.sequenceSubsequences = initialState.sequenceSubsequences;
    },
    resetSpammyEmailAccountsRequest: (state) => {
      state.getSpammyEmailAccountsRequest =
        initialState.getSpammyEmailAccountsRequest;
      state.spammyEmailAccountsResponse =
        initialState.spammyEmailAccountsResponse;
    },
    resetSpamAssassinReportRequest: (state) => {
      state.getSpamAssassinReportRequest =
        initialState.getSpamAssassinReportRequest;
      state.spamAssassinReportResponse =
        initialState.spamAssassinReportResponse;
    },
    resetTestReportEspsRequest: (state) => {
      state.getTestReportEspsRequest = initialState.getTestReportEspsRequest;
      state.testReportEspsResponse = initialState.testReportEspsResponse;
    },

    changeFireBaseConnected: (state, action) => {
      state.isFireBaseConnected = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Test Dashboard Graph Data
    builder.addCase(getTestGraphDataRequest.pending, (state) => {
      state.getTestGraphDataRequest.status = RequestStatus.Pending;
      state.getTestGraphDataRequest.error = null;
    });
    builder.addCase(getTestGraphDataRequest.fulfilled, (state, action) => {
      state.getTestGraphDataRequest.status = RequestStatus.Succeeded;
      state.getTestGraphDataRequest.message = action.payload.message;
      state.testGraphDataResponse = action.payload.payload;
      state.testGraphData = transformGraphDataResponse(action.payload.payload);
    });
    builder.addCase(getTestGraphDataRequest.rejected, (state, action) => {
      state.getTestGraphDataRequest.status = RequestStatus.Failed;
      state.getTestGraphDataRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Test Dashboard Summary Data
    builder.addCase(getTestSummaryDataRequest.pending, (state) => {
      state.getTestSummaryDataRequest.status = RequestStatus.Pending;
      state.getTestSummaryDataRequest.error = null;
    });
    builder.addCase(getTestSummaryDataRequest.fulfilled, (state, action) => {
      state.getTestSummaryDataRequest.status = RequestStatus.Succeeded;
      state.getTestSummaryDataRequest.message = action.payload.message;
      state.testSummaryData = action.payload.payload;
    });
    builder.addCase(getTestSummaryDataRequest.rejected, (state, action) => {
      state.getTestSummaryDataRequest.status = RequestStatus.Failed;
      state.getTestSummaryDataRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Test List Data
    builder.addCase(getTestListRequest.pending, (state) => {
      state.getTestListRequest.status = RequestStatus.Pending;
      state.getTestListRequest.error = null;
    });
    builder.addCase(getTestListRequest.fulfilled, (state, action) => {
      state.getTestListRequest.status = RequestStatus.Succeeded;
      state.getTestListRequest.message = action.payload.message;
      state.testListData = action.payload.payload;
    });
    builder.addCase(getTestListRequest.rejected, (state, action) => {
      state.getTestListRequest.status = RequestStatus.Failed;
      state.getTestListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update Test Status
    builder.addCase(updateTestStatusRequest.pending, (state) => {
      state.updateTestStatusRequest.status = RequestStatus.Pending;
      state.updateTestStatusRequest.error = null;
    });
    builder.addCase(updateTestStatusRequest.fulfilled, (state, action) => {
      state.updateTestStatusRequest.status = RequestStatus.Succeeded;
      state.updateTestStatusRequest.message = action.payload.message;
      state.updateTestStatusResponse = action.payload.payload;
    });
    builder.addCase(updateTestStatusRequest.rejected, (state, action) => {
      state.updateTestStatusRequest.status = RequestStatus.Failed;
      state.updateTestStatusRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // re run test
    builder.addCase(reRunTestRequest.pending, (state) => {
      state.reRunTestRequest.status = RequestStatus.Pending;
      state.reRunTestRequest.error = null;
    });
    builder.addCase(reRunTestRequest.fulfilled, (state, action) => {
      state.reRunTestRequest.status = RequestStatus.Succeeded;
      state.reRunTestRequest.message = action.payload.message;
      state.reRunTestResponse = action.payload.payload;
    });
    builder.addCase(reRunTestRequest.rejected, (state, action) => {
      state.reRunTestRequest.status = RequestStatus.Failed;
      state.reRunTestRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Test Preview Email
    builder.addCase(getTestPreviewEmailRequest.pending, (state) => {
      state.getTestPreviewEmailRequest.status = RequestStatus.Pending;
      state.getTestPreviewEmailRequest.error = null;
    });
    builder.addCase(getTestPreviewEmailRequest.fulfilled, (state, action) => {
      state.getTestPreviewEmailRequest.status = RequestStatus.Succeeded;
      state.getTestPreviewEmailRequest.message = action.payload.message;
      state.testPreviewEmailResponse = action.payload.payload;
    });
    builder.addCase(getTestPreviewEmailRequest.rejected, (state, action) => {
      state.getTestPreviewEmailRequest.status = RequestStatus.Failed;
      state.getTestPreviewEmailRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Test Report Export
    builder.addCase(getTestReportExportRequest.pending, (state) => {
      state.getTestReportExportRequest.status = RequestStatus.Pending;
      state.getTestReportExportRequest.error = null;
    });
    builder.addCase(getTestReportExportRequest.fulfilled, (state, action) => {
      state.getTestReportExportRequest.status = RequestStatus.Succeeded;
      state.getTestReportExportRequest.message = action.payload.message;
      state.testReportExportResponse = action.payload.payload;
    });
    builder.addCase(getTestReportExportRequest.rejected, (state, action) => {
      state.getTestReportExportRequest.status = RequestStatus.Failed;
      state.getTestReportExportRequest.error =
        !action.payload.isHandled && action.payload;
    });
    // Create Update Test
    builder.addCase(seedTestRequest.pending, (state) => {
      state.seedTestRequest.status = RequestStatus.Pending;
      state.seedTestRequest.error = null;
    });
    builder.addCase(seedTestRequest.fulfilled, (state, action) => {
      state.seedTestRequest.status = RequestStatus.Succeeded;
      state.seedTestRequest.message = action.payload.message;
      state.createUpdateTestResponse = action.payload.payload;
    });
    builder.addCase(seedTestRequest.rejected, (state, action) => {
      state.seedTestRequest.status = RequestStatus.Failed;
      state.seedTestRequest.error = !action.payload.isHandled && action.payload;
    });

    // Get Create Update Test
    builder.addCase(getSeedTestRequest.pending, (state) => {
      state.getSeedTestRequest.status = RequestStatus.Pending;
      state.getSeedTestRequest.error = null;
    });
    builder.addCase(getSeedTestRequest.fulfilled, (state, action) => {
      state.getSeedTestRequest.status = RequestStatus.Succeeded;
      state.getSeedTestRequest.message = action.payload.message;
      state.getCreateUpdateTestResponse = action.payload.payload;
    });
    builder.addCase(getSeedTestRequest.rejected, (state, action) => {
      state.getSeedTestRequest.status = RequestStatus.Failed;
      state.getSeedTestRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Update Lauch Test
    builder.addCase(updateSeedTestRequest.pending, (state) => {
      state.updateSeedTestRequest.status = RequestStatus.Pending;
      state.updateSeedTestRequest.error = null;
    });
    builder.addCase(updateSeedTestRequest.fulfilled, (state, action) => {
      state.updateSeedTestRequest.status = RequestStatus.Succeeded;
      state.updateSeedTestRequest.message = action.payload.message;
      // state.updateSeedTestRequest = action.payload.payload;
    });
    builder.addCase(updateSeedTestRequest.rejected, (state, action) => {
      state.updateSeedTestRequest.status = RequestStatus.Failed;
      state.updateSeedTestRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Seed Test Review Sending Limit
    builder.addCase(
      getSeedTestTemplateReviewSendingLimitRequest.pending,
      (state) => {
        state.getSeedTestTemplateReviewSendingLimitRequest.status =
          RequestStatus.Pending;
        state.getSeedTestTemplateReviewSendingLimitRequest.error = null;
      },
    );
    builder.addCase(
      getSeedTestTemplateReviewSendingLimitRequest.fulfilled,
      (state, action) => {
        state.getSeedTestTemplateReviewSendingLimitRequest.status =
          RequestStatus.Succeeded;
        state.getSeedTestTemplateReviewSendingLimitRequest.message =
          action.payload.message;

        const { page } = action?.meta?.arg || {};

        if (
          page &&
          page > 1 &&
          state.getSeedTestTemplateReviewSendingLimitResponse?.emailAccounts
        ) {
          state.getSeedTestTemplateReviewSendingLimitResponse = {
            ...action.payload.payload,
            emailAccounts: [
              ...(state.getSeedTestTemplateReviewSendingLimitResponse
                .emailAccounts || []),
              ...(action.payload.payload?.emailAccounts || []),
            ],
          };
        } else {
          state.getSeedTestTemplateReviewSendingLimitResponse =
            action.payload.payload;
        }
      },
    );
    builder.addCase(
      getSeedTestTemplateReviewSendingLimitRequest.rejected,
      (state, action) => {
        state.getSeedTestTemplateReviewSendingLimitRequest.status =
          RequestStatus.Failed;
        state.getSeedTestTemplateReviewSendingLimitRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Seed Test Warning Banner Config
    builder.addCase(
      getSeedTestTemplateWarningBannerConfigRequest.pending,
      (state) => {
        state.getSeedTestTemplateWarningBannerConfigRequest.status =
          RequestStatus.Pending;
        state.getSeedTestTemplateWarningBannerConfigRequest.error = null;
      },
    );
    builder.addCase(
      getSeedTestTemplateWarningBannerConfigRequest.fulfilled,
      (state, action) => {
        state.getSeedTestTemplateWarningBannerConfigRequest.status =
          RequestStatus.Succeeded;
        state.getSeedTestTemplateWarningBannerConfigResponse =
          action.payload.payload;
      },
    );
    builder.addCase(
      getSeedTestTemplateWarningBannerConfigRequest.rejected,
      (state, action) => {
        state.getSeedTestTemplateWarningBannerConfigRequest.status =
          RequestStatus.Failed;
        state.getSeedTestTemplateWarningBannerConfigRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get All Not Linked Email Accounts
    builder.addCase(
      getAllNotLinkedEmailAccountsInInboxRadarRequest.pending,
      (state) => {
        state.getAllNotLinkedEmailAccountsInInboxRadarRequest.status =
          RequestStatus.Pending;
        state.getAllNotLinkedEmailAccountsInInboxRadarRequest.message = null;
        state.getAllNotLinkedEmailAccountsInInboxRadarRequest.error = null;
      },
    );
    builder.addCase(
      getAllNotLinkedEmailAccountsInInboxRadarRequest.fulfilled,
      (state, action) => {
        state.getAllNotLinkedEmailAccountsInInboxRadarRequest.status =
          RequestStatus.Succeeded;
        state.getAllNotLinkedEmailAccountsInInboxRadarResponse =
          action.payload.payload;
      },
    );
    builder.addCase(
      getAllNotLinkedEmailAccountsInInboxRadarRequest.rejected,
      (state, action) => {
        state.getAllNotLinkedEmailAccountsInInboxRadarRequest.status =
          RequestStatus.Failed;
        state.getAllNotLinkedEmailAccountsInInboxRadarRequest.message =
          action.payload.message;
        state.getAllNotLinkedEmailAccountsInInboxRadarRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Not Linked Email Accounts Count
    builder.addCase(
      getNotLinkedEmailAccountsCountInInboxRadarRequest.pending,
      (state) => {
        state.getNotLinkedEmailAccountsCountInInboxRadarRequest.status =
          RequestStatus.Pending;
        state.getNotLinkedEmailAccountsCountInInboxRadarRequest.message = null;
        state.getNotLinkedEmailAccountsCountInInboxRadarRequest.error = null;
      },
    );
    builder.addCase(
      getNotLinkedEmailAccountsCountInInboxRadarRequest.fulfilled,
      (state, action) => {
        state.getNotLinkedEmailAccountsCountInInboxRadarRequest.status =
          RequestStatus.Succeeded;
        state.getNotLinkedEmailAccountsCountInInboxRadarResponse =
          action.payload.payload;
      },
    );
    builder.addCase(
      getNotLinkedEmailAccountsCountInInboxRadarRequest.rejected,
      (state, action) => {
        state.getNotLinkedEmailAccountsCountInInboxRadarRequest.status =
          RequestStatus.Failed;
        state.getNotLinkedEmailAccountsCountInInboxRadarRequest.message =
          action.payload.message;
        state.getNotLinkedEmailAccountsCountInInboxRadarRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Linked Email Accounts Request
    builder.addCase(
      getLinkedEmailAccountsInInboxAnalyzerRequest.pending,
      (state) => {
        state.getLinkedEmailAccountsInInboxAnalyzerRequest.status =
          RequestStatus.Pending;
        state.getLinkedEmailAccountsInInboxAnalyzerRequest.message = null;
        state.getLinkedEmailAccountsInInboxAnalyzerRequest.error = null;
      },
    );
    builder.addCase(
      getLinkedEmailAccountsInInboxAnalyzerRequest.fulfilled,
      (state, action) => {
        state.getLinkedEmailAccountsInInboxAnalyzerRequest.status =
          RequestStatus.Succeeded;
        state.getLinkedEmailAccountsInboxAnalyzerResponse =
          action.payload.payload;
      },
    );
    builder.addCase(
      getLinkedEmailAccountsInInboxAnalyzerRequest.rejected,
      (state, action) => {
        state.getLinkedEmailAccountsInInboxAnalyzerRequest.status =
          RequestStatus.Failed;
        state.getLinkedEmailAccountsInInboxAnalyzerRequest.message =
          action.payload.message;
        state.getLinkedEmailAccountsInInboxAnalyzerRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Add Remove Email Account Request
    builder.addCase(
      addRemoveLinkedEmailAccountsInInboxAnalyzerRequest.pending,
      (state) => {
        state.addRemoveLinkedEmailAccountRequest.status = RequestStatus.Pending;
        state.addRemoveLinkedEmailAccountRequest.message = null;
        state.addRemoveLinkedEmailAccountRequest.error = null;
      },
    );
    builder.addCase(
      addRemoveLinkedEmailAccountsInInboxAnalyzerRequest.fulfilled,
      (state, action) => {
        state.addRemoveLinkedEmailAccountRequest.status =
          RequestStatus.Succeeded;
        state.addRemoveLinkedEmailAccountRequest.message =
          action.payload.message;
      },
    );
    builder.addCase(
      addRemoveLinkedEmailAccountsInInboxAnalyzerRequest.rejected,
      (state, action) => {
        state.addRemoveLinkedEmailAccountRequest.status = RequestStatus.Failed;
        state.addRemoveLinkedEmailAccountRequest.message =
          action.payload.message;
        state.addRemoveLinkedEmailAccountRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Add Remove Email Account Request
    builder.addCase(draftSeedTestRequest.pending, (state) => {
      state.draftSeedTestRequest.status = RequestStatus.Pending;
      state.draftSeedTestRequest.message = null;
      state.draftSeedTestRequest.error = null;
    });
    builder.addCase(draftSeedTestRequest.fulfilled, (state, action) => {
      state.draftSeedTestRequest.status = RequestStatus.Succeeded;
      state.draftSeedTestRequest.message = action.payload.message;
      state.draftSeedTestResponse = action.payload.payload;
    });
    builder.addCase(draftSeedTestRequest.rejected, (state, action) => {
      state.addRemoveLinkedEmailAccountRequest.status = RequestStatus.Failed;
      state.addRemoveLinkedEmailAccountRequest.message = action.payload.message;
      state.addRemoveLinkedEmailAccountRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Test Config
    builder.addCase(getTestConfigRequest.pending, (state) => {
      state.getTestConfigRequest.status = RequestStatus.Pending;
      state.getTestConfigRequest.error = null;
    });
    builder.addCase(getTestConfigRequest.fulfilled, (state, action) => {
      state.getTestConfigRequest.status = RequestStatus.Succeeded;
      state.getTestConfigRequest.message = action.payload.message;
      state.testConfigResponse = action.payload.payload?.config;
      state.testReportLastRunList = action.payload.payload?.tests;
    });
    builder.addCase(getTestConfigRequest.rejected, (state, action) => {
      state.getTestConfigRequest.status = RequestStatus.Failed;
      state.getTestConfigRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Test Report Graph Data
    builder.addCase(getTestReportGraphDataRequest.pending, (state) => {
      state.getTestReportGraphDataRequest.status = RequestStatus.Pending;
      state.getTestReportGraphDataRequest.error = null;
    });
    builder.addCase(
      getTestReportGraphDataRequest.fulfilled,
      (state, action) => {
        state.getTestReportGraphDataRequest.status = RequestStatus.Succeeded;
        state.getTestReportGraphDataRequest.message = action.payload.message;
        state.testReportGraphDataResponse = action.payload.payload;
        state.testReportGraphData = transformGraphDataResponse(
          action.payload.payload?.analytics,
        );
      },
    );
    builder.addCase(getTestReportGraphDataRequest.rejected, (state, action) => {
      state.getTestReportGraphDataRequest.status = RequestStatus.Failed;
      state.getTestReportGraphDataRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Test Report Summary Data
    builder.addCase(getTestReportSummaryDataRequest.pending, (state) => {
      state.getTestReportSummaryDataRequest.status = RequestStatus.Pending;
      state.getTestReportSummaryDataRequest.error = null;
    });
    builder.addCase(
      getTestReportSummaryDataRequest.fulfilled,
      (state, action) => {
        state.getTestReportSummaryDataRequest.status = RequestStatus.Succeeded;
        state.getTestReportSummaryDataRequest.message = action.payload.message;
        state.testReportSummaryDataResponse = action.payload.payload;
      },
    );
    builder.addCase(
      getTestReportSummaryDataRequest.rejected,
      (state, action) => {
        state.getTestReportSummaryDataRequest.status = RequestStatus.Failed;
        state.getTestReportSummaryDataRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Test Report List Data
    builder.addCase(getTestReportListRequest.pending, (state) => {
      state.getTestReportListRequest.status = RequestStatus.Pending;
      state.getTestReportListRequest.error = null;
    });
    builder.addCase(getTestReportListRequest.fulfilled, (state, action) => {
      state.getTestReportListRequest.status = RequestStatus.Succeeded;
      state.getTestReportListRequest.message = action.payload.message;
      // Access page from action.meta.arg.payload (which is TestReportListRequestPayload)
      const page = action.meta.arg.payload?.page;

      if (page && page > 1 && state.testReportListData?.seedTests) {
        // If it's a subsequent page and existing data is present, append new items
        state.testReportListData = {
          ...action.payload.payload, // This should contain hasMore, total, etc.
          seedTests: [
            ...(state.testReportListData.seedTests || []),
            ...(action.payload.payload?.seedTests || []),
          ],
        };
      } else {
        // If it's the first page or no previous data, replace the data
        state.testReportListData = action.payload.payload;
      }
    });
    builder.addCase(getTestReportListRequest.rejected, (state, action) => {
      state.getTestReportListRequest.status = RequestStatus.Failed;
      state.getTestReportListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Test Report Emails Data
    builder.addCase(getTestReportEmailsRequest.pending, (state) => {
      state.getTestReportEmailsRequest.status = RequestStatus.Pending;
      state.getTestReportEmailsRequest.error = null;
    });
    builder.addCase(getTestReportEmailsRequest.fulfilled, (state, action) => {
      state.getTestReportEmailsRequest.status = RequestStatus.Succeeded;
      state.getTestReportEmailsRequest.message = action.payload.message;
      state.testReportEmailsResponse = action.payload.payload;
    });
    builder.addCase(getTestReportEmailsRequest.rejected, (state, action) => {
      state.getTestReportEmailsRequest.status = RequestStatus.Failed;
      state.getTestReportEmailsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Sequence Subsequence List
    builder.addCase(getSequenceSubsequenceListRequest.pending, (state) => {
      state.getSequenceSubsequenceListRequest.status = RequestStatus.Pending;
      state.getSequenceSubsequenceListRequest.error = null;
    });
    builder.addCase(
      getSequenceSubsequenceListRequest.fulfilled,
      (state, action) => {
        state.getSequenceSubsequenceListRequest.status =
          RequestStatus.Succeeded;
        state.getSequenceSubsequenceListRequest.message =
          action.payload.message;
        state.sequenceSubsequences = action.payload.payload;
      },
    );
    builder.addCase(
      getSequenceSubsequenceListRequest.rejected,
      (state, action) => {
        state.getSequenceSubsequenceListRequest.status = RequestStatus.Failed;
        state.getSequenceSubsequenceListRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Sequence Step List
    builder.addCase(getSequenceStepListRequest.pending, (state) => {
      state.getSequenceStepListRequest.status = RequestStatus.Pending;
      state.getSequenceStepListRequest.error = null;
    });
    builder.addCase(getSequenceStepListRequest.fulfilled, (state, action) => {
      state.getSequenceStepListRequest.status = RequestStatus.Succeeded;
      state.getSequenceStepListRequest.message = action.payload.message;
      state.sequenceSteps = action.payload.payload;
    });
    builder.addCase(getSequenceStepListRequest.rejected, (state, action) => {
      state.getSequenceStepListRequest.status = RequestStatus.Failed;
      state.getSequenceStepListRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Shareable Report Data
    builder.addCase(getShareableReportDataRequest.pending, (state) => {
      state.getShareableReportDataRequest.status = RequestStatus.Pending;
      state.getShareableReportDataRequest.error = null;
    });
    builder.addCase(
      getShareableReportDataRequest.fulfilled,
      (state, action) => {
        state.getShareableReportDataRequest.status = RequestStatus.Succeeded;
        state.getShareableReportDataRequest.message = action.payload.message;
        state.getShareableReportDataResponse = action.payload.payload;
      },
    );
    builder.addCase(getShareableReportDataRequest.rejected, (state, action) => {
      state.getShareableReportDataRequest.status = RequestStatus.Failed;
      state.getShareableReportDataRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Shareable Report Sender Email Accounts
    builder.addCase(
      getShareableReportSenderEmailAccountsRequest.pending,
      (state) => {
        state.getShareableReportSenderEmailAccountsRequest.status =
          RequestStatus.Pending;
        state.getShareableReportSenderEmailAccountsRequest.error = null;
      },
    );
    builder.addCase(
      getShareableReportSenderEmailAccountsRequest.fulfilled,
      (state, action) => {
        state.getShareableReportSenderEmailAccountsRequest.status =
          RequestStatus.Succeeded;
        state.getShareableReportSenderEmailAccountsRequest.message =
          action.payload.message;

        // Access page from action.meta.arg.payload (assuming structure { shareableReportId: string, payload: { page: number, limit: number } })
        // Adjust access to 'page' based on the actual structure of getShareableReportSenderEmailAccountsRequest arguments
        const page = action.meta.arg.payload?.page;

        if (
          page &&
          page > 1 &&
          state.getShareableReportSenderEmailAccountsResponse?.seedTests
        ) {
          // If it's a subsequent page and existing data is present, append new items
          state.getShareableReportSenderEmailAccountsResponse = {
            ...action.payload.payload, // This should contain hasMore, total, etc.
            seedTests: [
              ...(state.getShareableReportSenderEmailAccountsResponse
                .seedTests || []),
              ...(action.payload.payload?.seedTests || []),
            ],
          };
        } else {
          // If it's the first page or no previous data, replace the data
          state.getShareableReportSenderEmailAccountsResponse =
            action.payload.payload;
        }
      },
    );
    builder.addCase(
      getShareableReportSenderEmailAccountsRequest.rejected,
      (state, action) => {
        state.getShareableReportSenderEmailAccountsRequest.status =
          RequestStatus.Failed;
        state.getShareableReportSenderEmailAccountsRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Delete Test Report
    builder.addCase(deleteTestReportRequest.pending, (state) => {
      state.deleteTestReportRequest.status = RequestStatus.Pending;
      state.deleteTestReportRequest.error = null;
    });
    builder.addCase(deleteTestReportRequest.fulfilled, (state, action) => {
      state.deleteTestReportRequest.status = RequestStatus.Succeeded;
      state.deleteTestReportRequest.message = action.payload.message;
    });
    builder.addCase(deleteTestReportRequest.rejected, (state, action) => {
      state.deleteTestReportRequest.status = RequestStatus.Failed;
      state.deleteTestReportRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Email Reputation
    builder.addCase(getEmailReputationRequest.pending, (state) => {
      state.getEmailReputationRequest.status = RequestStatus.Pending;
      state.getEmailReputationRequest.error = null;
    });
    builder.addCase(getEmailReputationRequest.fulfilled, (state, action) => {
      state.getEmailReputationRequest.status = RequestStatus.Succeeded;
      state.getEmailReputationRequest.message = action.payload.message;

      // Access page from action.meta.arg.payload
      const page = action.meta.arg.payload?.page;

      if (page && page > 1 && state.emailReputationResponse?.emailAccounts) {
        // If it's a subsequent page and existing data is present, append new items
        const existingEmailAccounts =
          state.emailReputationResponse.emailAccounts || [];
        const newEmailAccounts = action.payload.payload?.emailAccounts || [];

        state.emailReputationResponse = {
          ...action.payload.payload,
          emailAccounts: [...existingEmailAccounts, ...newEmailAccounts],
        };
      } else {
        // If it's the first page or no previous data, replace the data
        state.emailReputationResponse = action.payload.payload;
      }
      state.hasMoreEmailReputation = action.payload.payload?.hasMore;
    });
    builder.addCase(getEmailReputationRequest.rejected, (state, action) => {
      state.getEmailReputationRequest.status = RequestStatus.Failed;
      state.getEmailReputationRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Spammy Email Accounts
    builder.addCase(getSpammyEmailAccountsRequest.pending, (state) => {
      state.getSpammyEmailAccountsRequest.status = RequestStatus.Pending;
      state.getSpammyEmailAccountsRequest.error = null;
    });
    builder.addCase(
      getSpammyEmailAccountsRequest.fulfilled,
      (state, action) => {
        state.getSpammyEmailAccountsRequest.status = RequestStatus.Succeeded;
        state.getSpammyEmailAccountsRequest.message = action.payload.message;
        state.spammyEmailAccountsResponse = action.payload.payload;
      },
    );
    builder.addCase(getSpammyEmailAccountsRequest.rejected, (state, action) => {
      state.getSpammyEmailAccountsRequest.status = RequestStatus.Failed;
      state.getSpammyEmailAccountsRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Spam Assassin Report
    builder.addCase(getSpamAssassinReportRequest.pending, (state) => {
      state.getSpamAssassinReportRequest.status = RequestStatus.Pending;
      state.getSpamAssassinReportRequest.error = null;
    });
    builder.addCase(getSpamAssassinReportRequest.fulfilled, (state, action) => {
      state.getSpamAssassinReportRequest.status = RequestStatus.Succeeded;
      state.getSpamAssassinReportRequest.message = action.payload.message;
      state.spamAssassinReportResponse = action.payload.payload;
    });
    builder.addCase(getSpamAssassinReportRequest.rejected, (state, action) => {
      state.getSpamAssassinReportRequest.status = RequestStatus.Failed;
      state.getSpamAssassinReportRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Test Report Esps Data
    builder.addCase(getTestReportEspsRequest.pending, (state) => {
      state.getTestReportEspsRequest.status = RequestStatus.Pending;
      state.getTestReportEspsRequest.error = null;
    });
    builder.addCase(getTestReportEspsRequest.fulfilled, (state, action) => {
      state.getTestReportEspsRequest.status = RequestStatus.Succeeded;
      state.getTestReportEspsRequest.message = action.payload.message;
      state.testReportEspsResponse = action.payload.payload;
    });
    builder.addCase(getTestReportEspsRequest.rejected, (state, action) => {
      state.getTestReportEspsRequest.status = RequestStatus.Failed;
      state.getTestReportEspsRequest.error =
        !action.payload.isHandled && action.payload;
    });
  },
});

export const {
  resetTestGraphData,
  resetCreateUpdateTestResponse,
  resetLinkInboxAnalyzerEmailAccountsState,
  resetDraftSeedTestState,
  resetUpdateSeedTestState,
  resetGetSeedTestTemplateReviewSendingLimitRequest,
  resetGetSeedTestTemplateWarningBannerConfigRequest,
  resetTestSummaryData,
  resetTestListRequest,
  resetUpdateTestStatusRequest,
  resetReRunTestRequest,
  resetTestConfigRequest,
  resetTestReportGraphRequest,
  resetTestReportSummaryRequest,
  resetTestReportListRequest,
  resetTestReportEmailsRequest,
  resetTestPreviewEmailRequest,
  resetTestReportExportRequest,
  resetGetShareableReportDataRequest,
  resetGetShareableReportSenderEmailAccountsRequest,
  resetGetCreateUpdateTestResponse,
  resetSequenceStepList,
  resetSequenceSubsequenceList,
  resetDeleteTestReportRequest,
  resetEmailReputationRequest,
  resetSpammyEmailAccountsRequest,
  resetSpamAssassinReportRequest,
  resetTestReportEspsRequest,
  changeFireBaseConnected,
} = inboxRadarSlice.actions;
export default inboxRadarSlice.reducer;
